/**
 * Plain-language names for trust policy v4 reasons, review outcomes and older
 * policy codes retained so historical proposals remain readable.
 */
import { translate } from "@/i18n";
import { libMemoryMessages } from "@/i18n/messages/lib-memory";
import type en from "@/i18n/messages/lib-memory/en";

type ReasonKey = Extract<keyof typeof en, `reason${string}`>;

const REVIEW_REASON_KEY: Readonly<Record<string, ReasonKey>> = {
  phase2_quarantine: "reasonPhase2Quarantine",
  review_required: "reasonReviewRequired",
  sensitive_review_required: "reasonSensitiveReviewRequired",
  conflict_review_required: "reasonConflictReviewRequired",
  possible_duplicate: "reasonPossibleDuplicate",
  correction_target_uncertain: "reasonCorrectionTargetUncertain",
  learned_from_work: "reasonLearnedFromWork",
  previously_rejected: "reasonPreviouslyRejected",
  explicit_fact: "reasonExplicitFact",
  explicit_profile: "reasonExplicitProfile",
  explicit_preference: "reasonExplicitPreference",
  explicit_named_entity: "reasonExplicitNamedEntity",
  explicit_remembered_process: "reasonExplicitRememberedProcess",
  explicit_correction: "reasonExplicitCorrection",
  credential_rejected: "reasonCredentialRejected",
  temporally_expired: "reasonTemporallyExpired",
  user_review: "reasonUserReview",
  user_forgotten: "reasonUserForgotten",
  superseded_by_activation: "reasonSupersededByActivation",
  proposal_expired: "reasonProposalExpired",
  source_deleted: "reasonSourceDeleted",
  model_changed: "reasonModelChanged",
  scope_uncertain: "reasonScopeUncertain",
  low_confidence: "reasonLowConfidence",
  fact_not_explicit: "reasonFactNotExplicit",
  inferred_entity: "reasonInferredEntity",
  single_source_inference: "reasonSingleSourceInference",
  business_assertion_review_required: "reasonBusinessAssertionReviewRequired",
  process_review_required: "reasonProcessReviewRequired",
  verified_process_review_required: "reasonVerifiedProcessReviewRequired",
  unsupported_candidate: "reasonUnsupportedCandidate",
  repeated_inferred_preference: "reasonRepeatedInferredPreference",
  explicit_scoped_business_context: "reasonExplicitScopedBusinessContext",
};

/** The reason in the app language; an unknown code reads as its words. */
export function reviewReasonLabel(reasonCode: string): string {
  if (!reasonCode) return translate(libMemoryMessages, "reasonReviewRequired");
  const key = REVIEW_REASON_KEY[reasonCode];
  return key
    ? translate(libMemoryMessages, key)
    : reasonCode.replace(/_/g, " ");
}
